import React, { ReactElement, useEffect, useState, useMemo } from 'react';
import { Modal, Button, Typography, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { isEmpty } from 'lodash';
import { ReactComponent as FollowUpAttachmentIcon } from '../../../assets/images/svg/FollowUpAttachmentIcon.svg';
import { ReactComponent as CrossCircleBlue } from '../../../assets/images/svg/cross_circle_blue.svg';
import '../../../shared/components/tags/inpatienList.less';
import ApplicationDetail from '../../../shared/services/applicationDetail.service';
import {
  followUpConfirmation,
  setFollowUpDetails,
} from '../../../store/actions/inPatientAction';
import { getValidEmails, validateFollowUpEmails } from '../../../utils/utility';
import { FOLLOW_CONSTANTS } from '../../constants/commonConstants';
import { ReactComponent as Close } from '../../../assets/images/svg/cross-circle.svg';

const { Text, Paragraph } = Typography;

const EmailRow = ({
  label,
  value,
  isSent,
}: {
  label: string;
  value: string | string[];
  isSent: boolean;
}): JSX.Element => (
  <div className="email-row">
    <Text className={isSent ? 'email-sent-label' : 'email-label'}>{label}</Text>
    <div className={isSent ? 'email-value email-sent-value' : 'email-value'}>
      <Text className={label === 'From :' ? 'from-email-value' : ''}>
        {Array.isArray(value) ? value?.join(', ') : value}
      </Text>
    </div>
  </div>
);

const AttachmentList = ({ files }: { files: string[] }): JSX.Element => (
  <div className="email-attachments">
    <Text className="attachment-text">Attachments :</Text>
    {files?.map(
      (file: string, idx: number): JSX.Element => (
        <span key={idx} className="email-attachment-tag">
          <FollowUpAttachmentIcon className="followUp-icon" />
          {file}
        </span>
      ),
    )}
  </div>
);

export const FollowUpModal = (props: any): ReactElement => {
  const { callInpatientList } = props;
  const { followUpDetails } = useSelector(
    (state: RootState): any => state?.selectedInPatientDetail || {},
  );
  const appDetail = ApplicationDetail?.instance;
  const hospitalDetails = useMemo((): any => appDetail.getHospitalDetail(), []);
  const { providerName, providerAddress } = hospitalDetails || {};
  const {
    fromEmailId = '',
    toEmailId = [],
    ccEmailId = [],
    subject = '',
    greetingBody = '',
    emailBody = '',
    attachments = [],
    documentGUIDs = [],
    statusID = 0,
  } = followUpDetails || {};
  const [visible, setVisible] = useState(false);
  const [sent, setSent] = useState(false);
  const [ccEmails, setCcEmails] = useState<string[]>(ccEmailId || []);
  const [showAcknowledgmentMsg, setShowAcknowledgmentMsg] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFollowUpSuccessfullySent, setIsFollowUpSuccessfullySent] =
    useState(false);
  const dispatch = useDispatch();

  useEffect((): void => {
    if (isEmpty(followUpDetails)) {
      setVisible(false);
    } else {
      setCcEmails(getValidEmails(ccEmailId));
      setVisible(true);
    }
    setShowAcknowledgmentMsg(false);
    setIsLoading(false);
  }, [followUpDetails]);

  const handleSend = (): void => {
    const followUpEmailPayload = {
      // payload to be sent to the backend to confirm follow up
      documentGUIDs,
      providerAddress,
      providerName,
      cCEmail: ccEmails,
      greetingBody,
      mailBody: emailBody,
      statusID,
      mailSubject: subject,
    };
    setIsLoading(true);

    const onSuccess = (): void => {
      setShowAcknowledgmentMsg(true); // to show the acknowledgment message
      setIsFollowUpSuccessfullySent(true); // to show the success message
      setIsLoading(false);
      callInpatientList();
    };

    const onFailure = (): void => {
      setShowAcknowledgmentMsg(true); // not to close the acknowledgment message on falilure
      setIsFollowUpSuccessfullySent(false); // to show the failure message
      setIsLoading(false);
    };
    dispatch(
      followUpConfirmation({ followUpEmailPayload, onSuccess, onFailure }),
    );
    setSent(true); // to move to next screen of the modal
  };

  const handleCcEmailChange = (emails: string[]): void => {
    if (emails?.length <= ccEmails?.length) {
      setCcEmails(emails);
      return;
    }
    if (validateFollowUpEmails(emails, ccEmails)) {
      setCcEmails(emails);
    }
  };

  const tagRender = (tagProps: any): ReactElement => {
    // Custom tag rendering for the cc mail
    const { label, closable, onClose } = tagProps;
    return (
      <div className="email-tag">
        {label}
        {closable && (
          <CrossCircleBlue
            className="cross-icon mouse-pointer email-tag-cancel"
            onClick={onClose}
          />
        )}
      </div>
    );
  };

  const renderEditableForm = (): JSX.Element => (
    <>
      <EmailRow label="From :" value={fromEmailId} isSent={false} />
      <hr className="hr-followup" />
      <EmailRow label="To :" value={toEmailId} isSent={false} />
      <div className="email-row">
        <Text className="email-label">Cc :</Text>
        <Select
          mode="tags"
          className="email-tag-select"
          placeholder="Enter Cc emails"
          value={ccEmails}
          onChange={handleCcEmailChange}
          tagRender={tagRender}
          tokenSeparators={[',']}
          open={false}
        />
      </div>
      <hr className="hr-followup" />
      <div>
        <Text className="email-subject">Subject :</Text>
        <Text>{subject}</Text>
      </div>
      <div className="email-body-box">
        <Paragraph className="email-body-text">{greetingBody},</Paragraph>
        <Paragraph className="email-body-text">{emailBody}</Paragraph>
        <Paragraph className="email-body-signature">
          From <br />
          {providerName},<br />
          {providerAddress}
        </Paragraph>
      </div>
      <AttachmentList files={attachments} />
      <div className="email-send-button">
        <Button type="primary" className="send-btn-width" onClick={handleSend}>
          Send
        </Button>
      </div>
    </>
  );

  const renderSentPreview = (): JSX.Element => (
    <>
      <Text className="sent-email-subject">{subject}</Text>
      <hr className="hr-followup" />
      <div className="email-from-row">
        <div className="email-avatar">
          <Text className="email-avatar-text">
            {fromEmailId?.charAt(0)?.toUpperCase()}
          </Text>
        </div>
        <Text>{fromEmailId}</Text>
      </div>
      <EmailRow label="To :" value={toEmailId} isSent={true} />
      <EmailRow label="Cc :" value={ccEmails} isSent={true} />
      <hr className="hr-followup" />
      <div className="email-body-box">
        <Paragraph>{greetingBody},</Paragraph>
        <Paragraph>{emailBody}</Paragraph>
        <Paragraph className="email-body-signature">
          From <br />
          {providerName || ''},<br />
          {providerAddress || ''}
          <br />
        </Paragraph>
      </div>
      <AttachmentList files={attachments} />
      {showAcknowledgmentMsg && (
        <div className="email-success-msg">
          <Text
            className={`${
              isFollowUpSuccessfullySent
                ? 'success-msg-color'
                : 'failure-msg-color'
            } msg-text`}
          >
            {isFollowUpSuccessfullySent
              ? FOLLOW_CONSTANTS.FOLLOW_UP_SUCCESS_MSG
              : FOLLOW_CONSTANTS.FOLLOW_UP_FAILURE_MSG}
          </Text>
        </div>
      )}
    </>
  );

  return (
    <Modal
      title={'Send a Follow Up'}
      visible={visible}
      onCancel={(): void => {
        setVisible(false);
        dispatch(setFollowUpDetails({}));
        setSent(false);
      }}
      footer={null}
      width={688}
      className="email-modal"
      maskClosable={!isLoading}
      closable={!isLoading}
      closeIcon={isLoading ? null : <Close className="follow-up-close-icon" />}
    >
      {sent ? renderSentPreview() : renderEditableForm()}
    </Modal>
  );
};
